import React from 'react';
import { Link } from 'react-router-dom';

import DocsLayout, { CodeBlock, pagerFor } from 'Containers/MCP/docs/DocsLayout';
import { MCP_ENDPOINT } from 'data/mcpTools';

import './reference.scss';

const PATH = '/mcp/docs/troubleshooting';
const { prev: PREV, next: NEXT } = pagerFor(PATH);

const TOC = [
  { href: '#symptoms', label: 'Common symptoms' },
  { href: '#faq', label: 'FAQ' },
];

const Troubleshooting = () => (
  <DocsLayout
    eyebrow="Support"
    title="Troubleshooting & FAQ"
    lede={
      <>
        Start with the symptom that matches what you are seeing. Almost every
        issue comes down to the connector URL, the brand selection, or an expired
        sign-in &mdash; all three are quick to fix from the Claude or Vocable
        interface.
      </>
    }
    toc={TOC}
    prev={PREV}
    next={NEXT}
  >
    <div className="mcp-ref">
      <h2 id="symptoms">Common symptoms</h2>

      <ul className="mcp-ref__symptoms">
        <li className="mcp-ref__symptom">
          <h3 id="no-brand-selected">
            The connector says it is connected, but every tool fails or says no
            brand is selected
          </h3>
          <p>
            This is expected the first time you use the connector if your account
            has more than one brand. Tools are scoped to a brand, and until one
            is active there is nothing for them to run against.
          </p>
          <p className="mcp-ref__fix-label">Fix</p>
          <ol className="mcp-ref__fix-steps">
            <li>
              Ask Claude to list your brands (&ldquo;list my Vocable
              brands&rdquo;).
            </li>
            <li>
              Ask it to set the one you want as the default (&ldquo;set Acme as
              my default brand&rdquo;).
            </li>
            <li>Retry the request that failed.</li>
          </ol>
          <p>
            The brand stays selected for the rest of your work, and you can
            switch at any time by asking again. See{' '}
            <Link to="/mcp/docs/tools#brands">Brands</Link> in the tool
            reference.
          </p>
        </li>

        <li className="mcp-ref__symptom">
          <h3 id="authorization-failed">
            Authorization failed when adding the connector
          </h3>
          <p>
            Nearly always the URL. The connector URL must be exact, including the
            path at the end &mdash; a bare{' '}
            <code className="mcp-docs__code-inline">/mcp</code> will not work.
          </p>
          <CodeBlock code={MCP_ENDPOINT} label="Connector URL" />
          <p className="mcp-ref__fix-label">Fix</p>
          <ol className="mcp-ref__fix-steps">
            <li>
              Copy the URL above and compare it character for character with what
              you entered &mdash; no trailing slash, no missing{' '}
              <code className="mcp-docs__code-inline">/mcp/v1</code>.
            </li>
            <li>Remove the connector in Claude&rsquo;s settings and add it again.</li>
            <li>
              When the Vocable sign-in page opens, complete it in that same
              browser &mdash; do not switch browsers or open it in a different
              profile part-way through.
            </li>
          </ol>
        </li>

        <li className="mcp-ref__symptom">
          <h3 id="stopped-working">It worked, then stopped after a while</h3>
          <p>
            Access tokens are valid for 24 hours. Once one expires the connector
            needs to authorise again, which can surface as tools suddenly failing
            after a day of not using them.
          </p>
          <p className="mcp-ref__fix-label">Fix</p>
          <ol className="mcp-ref__fix-steps">
            <li>
              Start a fresh request; if Claude prompts you to sign in to Vocable,
              complete the sign-in.
            </li>
            <li>
              If no prompt appears, remove the connector in Claude&rsquo;s
              settings and add it again with the URL above.
            </li>
          </ol>
        </li>

        <li className="mcp-ref__symptom">
          <h3 id="connected-but-unauthorised">
            The connector still shows as connected, but every tool returns an
            authorisation error
          </h3>
          <p>
            This is the same expiry as above wearing a different mask. A
            connection holds the sign-in it was given when it started, so a long
            conversation can keep running against a token that has since aged
            out. The connector list still says connected and the tools are still
            listed &mdash; only the calls fail.
          </p>
          <p className="mcp-ref__fix-label">Fix</p>
          <ol className="mcp-ref__fix-steps">
            <li>
              Start a new chat and retry there. A fresh conversation picks up a
              fresh sign-in, which resolves it in most cases.
            </li>
            <li>
              If a new chat fails the same way, reconnect the connector in
              Claude&rsquo;s settings, then retry.
            </li>
          </ol>
        </li>

        <li className="mcp-ref__symptom">
          <h3 id="signed-out-mid-setup">
            I got signed out mid-setup, or landed on the Vocable app home instead
            of finishing
          </h3>
          <p>
            The sign-in has to complete for the connection to be granted. If you
            end up on the Vocable dashboard rather than back in Claude, the flow
            did not finish and the connector will be left half-configured.
          </p>
          <p className="mcp-ref__fix-label">Fix</p>
          <ol className="mcp-ref__fix-steps">
            <li>
              Sign in to Vocable in your browser first, so you are already
              authenticated.
            </li>
            <li>Remove the connector in Claude&rsquo;s settings.</li>
            <li>
              Add it again, and follow the Vocable prompt all the way through
              until Claude shows the connector as connected.
            </li>
          </ol>
        </li>

        <li className="mcp-ref__symptom">
          <h3 id="cannot-find-content">Claude cannot find my content</h3>
          <p>
            The agent can only see what exists in the brand that is currently
            selected. An empty-looking library is usually the wrong brand, or a
            brand whose content and training have not been set up yet.
          </p>
          <p className="mcp-ref__fix-label">Fix</p>
          <ol className="mcp-ref__fix-steps">
            <li>
              Ask Claude which brand is active, and switch brands if it is not the
              one you expect.
            </li>
            <li>
              Open that brand in the Vocable app and confirm your documents and
              brand training are in place &mdash; voice, guidelines and content
              samples.
            </li>
            <li>Ask again once the brand has content to work from.</li>
          </ol>
          <p>
            <Link to="/mcp/docs/resources">Resources</Link> lists the brand
            context the agent reads, so you can see what a fully set-up brand
            offers it.
          </p>
        </li>
      </ul>

      <h2 id="faq">FAQ</h2>

      <dl className="mcp-ref__faq">
        <dt>Which clients can I use this with?</dt>
        <dd>
          Claude on the web and Claude Desktop. Connectors are brokered through
          your Claude account, so adding it once covers both. Claude Code and
          Cursor are not supported yet &mdash;{' '}
          <Link to="/mcp/docs/clients">supported clients</Link> explains what is
          blocking each of them.
        </dd>

        <dt>Does it cost extra?</dt>
        <dd>
          No. The connector works with your existing Vocable account and is
          subject to that account&rsquo;s normal limits and entitlements &mdash;
          the same ones that apply when you use the Vocable app directly. Some
          capabilities depend on what your plan already includes.
        </dd>

        <dt>Is this the same thing as the Vocable API?</dt>
        <dd>
          No. The API is for developers building their own integrations against
          Vocable. This is a hosted connector: nothing to run or deploy, you just
          add the URL in Claude and sign in.
        </dd>

        <dt>Can my teammates use it too?</dt>
        <dd>
          Yes. Each person adds the connector themselves and signs in with their
          own Vocable account, so everyone acts with their own access and
          permissions. You cannot share one connection between people.
        </dd>

        <dt>Why can the agent see one brand and not another?</dt>
        <dd>
          Because it acts as you. It has exactly the brand access your Vocable
          account has &mdash; no more. See{' '}
          <Link to="/mcp/docs/security">Authentication &amp; security</Link>.
        </dd>

        <dt>How do I stop Claude from reaching my Vocable account?</dt>
        <dd>
          Remove the Vocable connector in Claude&rsquo;s settings. Access ends
          immediately, and nothing in your Vocable account is deleted.
        </dd>

        <dt>Something else is wrong &mdash; how do I report it?</dt>
        <dd>
          Contact Vocable support at{' '}
          <a href="mailto:support@vocable.ai">support@vocable.ai</a>. It helps to
          say which client you were using, what you asked for, and what the agent
          replied &mdash; including any tool name it mentioned.
        </dd>
      </dl>
    </div>
  </DocsLayout>
);

export default Troubleshooting;
